import { useState } from "react";
import { Link } from "react-router-dom";
import { MailCheck } from "lucide-react";
import { Logo } from "@/components/common/Logo";
import api, { humanError } from "@/lib/api";

/**
 * Request a reset link.
 *
 * The confirmation is shown whether or not the address has an account,
 * matching what the API says. Telling someone "no account with that email"
 * turns this page into a way to find out which of a company's staff are
 * registered.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/password/reset-request", { email });
      setSent(true);
    } catch (err) {
      setError(humanError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="hs-mint-gradient flex min-h-screen items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center"><Logo /></div>
        <div className="rounded-2xl border border-[#E4E9EF] bg-white p-8 hs-card-shadow">
          {sent ? (
            <div data-testid="forgot-sent">
              <span className="mb-4 inline-grid h-11 w-11 place-items-center rounded-full bg-[#E8FBF1] text-[#16A46B]">
                <MailCheck className="h-5 w-5" />
              </span>
              <h1 className="font-display text-2xl font-bold text-[#0B2B33]">Check your inbox</h1>
              <p className="mt-2 text-sm text-[#43535E]">
                If <span className="font-semibold text-[#0B2B33]">{email}</span> has an account,
                a link to set a new password is on its way. It works once and expires in an hour.
              </p>
              <p className="mt-4 text-sm text-[#43535E]">
                Nothing arrived? Check spam, or{" "}
                <button
                  type="button"
                  onClick={() => setSent(false)}
                  className="font-semibold text-[#1F5FD8] hover:underline"
                  data-testid="forgot-try-again"
                >
                  try another address
                </button>.
              </p>
              <Link to="/login" className="hs-btn-primary mt-6 inline-flex w-full items-center justify-center">
                Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-display text-2xl font-bold text-[#0B2B33]">Forgot your password?</h1>
              <p className="mt-1 text-sm text-[#43535E]">
                Enter the address you sign in with and we will send a link to set a new one.
              </p>
              <form onSubmit={onSubmit} className="mt-6 space-y-4">
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A97A0]" htmlFor="email">
                    Email
                  </label>
                  <input
                    id="email" type="email" required autoFocus value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="forgot-email-input" autoComplete="email"
                    className="w-full rounded-lg border border-[#E4E9EF] px-3 py-2.5 text-sm text-[#0B2B33] focus:border-[#1F5FD8] focus:outline-none focus:ring-2 focus:ring-[#1F5FD8]/20"
                    placeholder="you@company.com"
                  />
                </div>
                {error ? <p className="text-sm text-[#B02328]" role="alert">{error}</p> : null}
                <button type="submit" disabled={busy} data-testid="forgot-submit"
                  className="hs-btn-primary w-full disabled:opacity-60">
                  {busy ? "Sending…" : "Send the link"}
                </button>
              </form>
            </>
          )}
        </div>
        <p className="mt-5 text-center text-sm text-[#43535E]">
          Remembered it? <Link to="/login" className="font-semibold text-[#1F5FD8] hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
